'use client'

import { useState } from 'react'
import { AvatarImage, Avatar, AvatarFallback } from '@/components/ui/avatar'
import { CardContent, Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { formatCurrency } from '@/lib/utils'
import {
  StreamableValue,
  useActions,
  useStreamableValue,
  useUIState
} from 'ai/rsc'
import { AI } from '@/app/action'
import { SearchProductsToolResult } from '@/lib/utils/vespa'
import { usePostHog } from 'posthog-js/react'

export interface SearchResultsProps {
  results: SearchProductsToolResult[]
  aggregateResultsStream: StreamableValue<{ totalResults: number }>
}

export function SearchResults({
  results,
  aggregateResultsStream
}: SearchResultsProps) {
  // State to manage whether to display the results
  const [showAllResults, setShowAllResults] = useState(false)
  const [aggregateResults] = useStreamableValue(aggregateResultsStream)
  const [messagesUIState, setMessagesUIState] = useUIState<typeof AI>()
  const posthog = usePostHog()

  const { submit } = useActions()

  const handleViewMore = () => {
    setShowAllResults(true)
    // Track "View More" click event
    posthog.capture('view_more_results_clicked')
  }

  const handleProductClick = (product: SearchProductsToolResult) => {
    // Track product card click event
    posthog.capture('mini_product_card_clicked', {
      product_url: product.productUrl,
      mongo_id: product.mongoId,
      product_title:
        `${product.year || ''} ${product.make} ${product.model}`.trim(),
      product_price: product.price,
      product_seller: product.seller
    })
  }

  const displayedResults = showAllResults ? results : results.slice(0, 3)
  const additionalResultsCount = results.length > 3 ? results.length - 3 : 0

  const buttonText = 'Show More Options'
  return (
    <div className="flex flex-wrap">
      {displayedResults.map((result, index) => {
        const year = result.year
        const make = result.make
        const model = result.model
        const productTitle = `${year || ''} ${make} ${model}`.trim()
        // TODO: Add price and hours to card
        const price = formatCurrency(result.price)

        return (
          <div className="flex flex-1 p-1" key={index}>
            <Link
              className="flex flex-1"
              href={result.productUrl}
              passHref
              target="_blank"
              onClick={() => handleProductClick(result)}
            >
              <Card className="flex-1">
                <CardContent className="p-0">
                  <img
                    alt={`${productTitle} image`}
                    src={result.primaryImage}
                    className="h-24 w-full min-w-24 object-cover"
                  />
                  <div className="flex flex-col gap-2 p-2">
                    <p className="line-clamp-2 text-xs">{productTitle}</p>
                    <div className="text-xs font-semibold">{price}</div>
                    <div className="flex items-center space-x-2">
                      <div className="line-clamp-2 text-xs opacity-60">
                        {result.seller}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        )
      })}
      {!showAllResults && additionalResultsCount > 0 && (
        <div className="w-1/2 p-1 md:w-1/4">
          <Card className="flex h-full flex-1 items-center justify-center">
            <CardContent className="p-2">
              <Button
                variant={'link'}
                className="text-muted-foreground"
                onClick={handleViewMore}
              >
                See {additionalResultsCount} more
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      {showAllResults && (
        <button
          className={`mt-6 w-full rounded-lg border border-gray-200 px-4 py-2 font-semibold text-zinc-900 disabled:bg-gray-300`}
          onClick={async () => {
            // Track "Show More Options" button click
            posthog.capture('show_more_options_clicked')

            // const response = await confirmPurchase(symbol, price, value)
            // setPurchasingUI(response.purchasingUI)
            // Insert a new system message to the UI.
            // setMessages((currentMessages: any) => [
            //   ...currentMessages,
            //   response.newMessage
            // ])

            // Remove the last 4 messages from AIState
            // setAIState(aiState => ({
            //   ...aiState,
            //   messages: aiState.messages.slice(0, aiState.messages.length - 4)
            // }))
            const formData = new FormData()
            formData.append('input', 'Show Me More')

            const responseMessage = await submit(formData)
            setMessagesUIState(currentMessages => [
              ...currentMessages,
              responseMessage as any
            ])

            // setUIState(uiState => [...uiState, response])
          }}
        >
          {buttonText}
        </button>
      )}
    </div>
  )
}
